import { Injectable, inject, signal, computed, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, map, tap, catchError, of } from 'rxjs';
import { ApiService } from './api.service';
import { StorageService } from './storage.service';
import { NotificationService } from './notification.service';
import { LicenseService } from './license.service';
import { CafeConfigService } from './cafe-config.service';
import { ApiResponse } from '../models/api.model';
import { LoginRequest, LoginResponse, User, RegisterOwnerRequest, SetupCafeRequest, VerifyOtpRequest, UpdateWorkflowRequest } from '../models/auth.model';
import * as Sentry from '@sentry/angular';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  // Auth State Signals
  currentUser = signal<User | null>(null);
  isLoggedIn = computed(() => !!this.currentUser());
  userRole = computed(() => this.currentUser()?.role);
  cafeId = computed(() => this.currentUser()?.cafeId);
  cafeSlug = computed(() => this.currentUser()?.cafeSlug);

  private injector = inject(Injector);

  constructor(
    private api: ApiService,
    private storage: StorageService,
    private router: Router,
    private notification: NotificationService,
    private cafeConfig: CafeConfigService
  ) {
    this.initFromStorage();
  }

  private clearLicenseState() {
    try {
      const licenseService = this.injector.get(LicenseService);
      licenseService.clearLicense();
    } catch (e) {
      localStorage.removeItem('bf_license_cache');
    }
  }

  private setSentryUser(user: User) {
    if (user) {
      Sentry.setUser({ username: user.email, id: user.id });
      Sentry.setTag('userRole', user.role);
      if (user.cafeId) Sentry.setTag('cafeId', user.cafeId);
    } else {
      Sentry.setUser(null);
    }
  }

  login(request: LoginRequest): Observable<LoginResponse> {
    return this.api.post<LoginResponse>('/auth/login', request).pipe(
      map(response => {
        const { accessToken, refreshToken, user } = response.data;
        this.storage.setItem(StorageService.ACCESS_TOKEN, accessToken);
        this.storage.setItem(StorageService.REFRESH_TOKEN, refreshToken);
        this.storage.setItem(StorageService.CURRENT_USER, JSON.stringify(user));
        this.clearLicenseState();
        this.currentUser.set(user);
        this.setSentryUser(user);
        return response.data;
      })
    );
  }

  registerOwner(request: RegisterOwnerRequest): Observable<LoginResponse> {
    return this.api.post<LoginResponse>('/auth/register', request).pipe(
      map(response => {
        const { accessToken, refreshToken, user } = response.data;
        this.storage.setItem(StorageService.ACCESS_TOKEN, accessToken);
        this.storage.setItem(StorageService.REFRESH_TOKEN, refreshToken);
        this.storage.setItem(StorageService.CURRENT_USER, JSON.stringify(user));
        this.clearLicenseState();
        this.currentUser.set(user);
        this.setSentryUser(user);
        return response.data;
      })
    );
  }

  register(request: any): Observable<LoginResponse> {
    return this.registerOwner(request);
  }

  setupCafe(request: SetupCafeRequest): Observable<LoginResponse> {
    return this.api.post<LoginResponse>('/auth/setup-cafe', request).pipe(
      map(response => {
        const { accessToken, refreshToken, user } = response.data;
        this.storage.setItem(StorageService.ACCESS_TOKEN, accessToken);
        this.storage.setItem(StorageService.REFRESH_TOKEN, refreshToken);
        this.storage.setItem(StorageService.CURRENT_USER, JSON.stringify(user));
        this.clearLicenseState();
        this.currentUser.set(user);
        this.setSentryUser(user);
        return response.data;
      })
    );
  }

  verifyOtp(request: VerifyOtpRequest): Observable<LoginResponse> {
    return this.api.post<LoginResponse>('/auth/verify-otp', request).pipe(
      map(response => {
        const { accessToken, refreshToken, user } = response.data;
        this.storage.setItem(StorageService.ACCESS_TOKEN, accessToken);
        this.storage.setItem(StorageService.REFRESH_TOKEN, refreshToken);
        this.storage.setItem(StorageService.CURRENT_USER, JSON.stringify(user));
        this.currentUser.set(user);
        this.setSentryUser(user);
        return response.data;
      })
    );
  }

  resendOtp(): Observable<any> {
    return this.api.post<any>('/auth/resend-otp', {});
  }

  updateWorkflowConfig(request: UpdateWorkflowRequest): Observable<any> {
    return this.api.put<any>('/auth/workflow-config', request);
  }

  requestPasswordReset(email: string): Observable<any> {
    return this.api.post<any>('/auth/forgot-password', { email });
  }

  resetPassword(token: string, newPassword: string): Observable<any> {
    return this.api.post<any>('/auth/reset-password', { token, newPassword });
  }

  logout(): void {
    this.storage.clear();
    this.currentUser.set(null);
    this.cafeConfig.clear();
    this.clearLicenseState();
    this.setSentryUser(null as any);
    this.router.navigate(['/auth/login']);
  }

  refreshToken(): Observable<any> {
    const refreshToken = this.storage.getItem(StorageService.REFRESH_TOKEN);
    if (!refreshToken) return of(null);

    return this.api.post<LoginResponse>('/auth/refresh', { refreshToken }).pipe(
      tap((response: ApiResponse<LoginResponse>) => {
        const { accessToken, refreshToken: newRefreshToken } = response.data;
        this.storage.setItem(StorageService.ACCESS_TOKEN, accessToken);
        this.storage.setItem(StorageService.REFRESH_TOKEN, newRefreshToken);
      }),
      catchError(err => {
        this.logout();
        throw err;
      })
    );
  }

  initFromStorage(): void {
    const userJson = this.storage.getItem(StorageService.CURRENT_USER);
    if (userJson) {
      try {
        const user = JSON.parse(userJson);
        this.currentUser.set(user);
        this.setSentryUser(user);
      } catch (e) {
        this.logout();
      }
    }
  }

  hasRole(role: string): boolean {
    const currentRole = this.userRole();
    return currentRole?.toUpperCase() === role.toUpperCase();
  }

  navigateByRole(user: User): void {
    if (!user || !user.role) {
      console.error('[AuthService] No user role found during navigation');
      this.router.navigate(['/auth/login']);
      return;
    }

    const role = user.role.toUpperCase();
    console.log(`[AuthService] Navigating based on role: ${role}`);

    let targetUrl = '/';

    if (role === 'OWNER' || role === 'STAFF') {
      if (!user.cafeId) {
        targetUrl = '/setup';
      } else {
        targetUrl = '/owner/dashboard';
      }
    } else if (role === 'SUPER_ADMIN') {
      targetUrl = '/admin/dashboard';
    } else if (role === 'CUSTOMER' && user.cafeSlug) {
      targetUrl = `/cafe/${user.cafeSlug}/menu`;
    }

    console.log(`[AuthService] Target URL determined: ${targetUrl}`);
    this.notification.info(`Detected role: ${role}. Redirecting...`);
    
    this.router.navigate([targetUrl]).then(success => {
      if (success) {
        console.log(`[AuthService] Successfully navigated to ${targetUrl}`);
      } else {
        console.error(`[AuthService] Navigation to ${targetUrl} was REJECTED. Is a Guard blocking it?`);
        this.notification.error('Access denied to the dashboard.');
      }
    }).catch(err => {
      console.error(`[AuthService] Navigation ERROR to ${targetUrl}. Full error details:`, err);
      if (err.stack) console.error('[AuthService] Stack trace:', err.stack);
      this.notification.error('A navigation error occurred. Check console for details.');
    });
  }
}
